import { Permissions, PermissionsFriendlyNames } from "../models/Enum";
import { SelectedFilters } from "../models/Items/ItemListRequest";
import { ProductFilterOptions } from "../models/Items/FilterOptions";
import {
  MULTI_SELECT_FIELD_NAMES,
  NO_SL_INTRO_DATE,
  NO_TAGS_OPTION,
  NO_VENDOR,
  PRODUCT_TYPE_DROPDOWN_OPTIONS,
  VIEW_INACTIVE_PRODUCTS,
  VIEW_PRODUCT_ASSIGNMENT,
  VIEW_TOOLINGS,
} from "../pages/Products/EditItem/Constants";
import { sortCVLFieldOptions } from "./helpers";

/**
 * Query parameter key for requesting a single dropdown's options.
 */
export const DYNAMIC_FILTERS_FIELD_QUERY_PARAM = "fieldName";

/** Backend-valid query param values for `fieldName`. Casing must match exactly. */
export const DYNAMIC_FILTER_QUERY_FIELD_NAMES = {
  stockCode: "Stockcode",
  baseCode: "BaseCode",
  pdCode: "PdCode",
  slIntroDate: "SLIntroDate",
  pdFamily: "PDFamilyName",
  brandCollection: "BrandCollection",
  stage: "Stage",
  status: "Status",
  marketingDesigner: "MarketingDesigner",
  seriesName: "SeriesName",
  category: "Category",
  function: "Function",
  primaryMaterial: "PrimaryMaterial",
  secondaryMaterial: "Secondarymaterial",
  vendor: "Vendor",
  itemStatus: "ItemStatus",
  tag: "Tag",
  assignment: "Assignment",
  user: "User",
  quoteDate: "QuoteDate",
  quoteApprovalType: "QuoteApprovalType",
  attachmentType: "AttachmentType",
} as const;

const UI_FIELD_TO_QUERY_FIELD_NAME: Record<string, string> = {
  brandCollection: DYNAMIC_FILTER_QUERY_FIELD_NAMES.brandCollection,
  [MULTI_SELECT_FIELD_NAMES.stage]: DYNAMIC_FILTER_QUERY_FIELD_NAMES.stage,
  [MULTI_SELECT_FIELD_NAMES.status]: DYNAMIC_FILTER_QUERY_FIELD_NAMES.status,
  [MULTI_SELECT_FIELD_NAMES.marketingDesigner]: DYNAMIC_FILTER_QUERY_FIELD_NAMES.marketingDesigner,
  [MULTI_SELECT_FIELD_NAMES.seriesName]: DYNAMIC_FILTER_QUERY_FIELD_NAMES.seriesName,
  [MULTI_SELECT_FIELD_NAMES.pdFamily]: DYNAMIC_FILTER_QUERY_FIELD_NAMES.pdFamily,
  category: DYNAMIC_FILTER_QUERY_FIELD_NAMES.category,
  [MULTI_SELECT_FIELD_NAMES.function]: DYNAMIC_FILTER_QUERY_FIELD_NAMES.function,
  baseCode: DYNAMIC_FILTER_QUERY_FIELD_NAMES.baseCode,
  pdCode: DYNAMIC_FILTER_QUERY_FIELD_NAMES.pdCode,
  stockCode: DYNAMIC_FILTER_QUERY_FIELD_NAMES.stockCode,
  primaryMaterial: DYNAMIC_FILTER_QUERY_FIELD_NAMES.primaryMaterial,
  secondaryMaterial: DYNAMIC_FILTER_QUERY_FIELD_NAMES.secondaryMaterial,
  [MULTI_SELECT_FIELD_NAMES.slIntroDate]: DYNAMIC_FILTER_QUERY_FIELD_NAMES.slIntroDate,
  [MULTI_SELECT_FIELD_NAMES.tags]: DYNAMIC_FILTER_QUERY_FIELD_NAMES.tag,
  [MULTI_SELECT_FIELD_NAMES.vendor]: DYNAMIC_FILTER_QUERY_FIELD_NAMES.vendor,
  attachmentTypeId: DYNAMIC_FILTER_QUERY_FIELD_NAMES.attachmentType,
  quoteDate: DYNAMIC_FILTER_QUERY_FIELD_NAMES.quoteDate,
  quoteApprovalType: DYNAMIC_FILTER_QUERY_FIELD_NAMES.quoteApprovalType,
  assignment: DYNAMIC_FILTER_QUERY_FIELD_NAMES.assignment,
  assignmentId: DYNAMIC_FILTER_QUERY_FIELD_NAMES.assignment,
  userId: DYNAMIC_FILTER_QUERY_FIELD_NAMES.user,
  itemStatus: DYNAMIC_FILTER_QUERY_FIELD_NAMES.itemStatus,
};

const QUERY_FIELD_NAME_TO_FILTER_OPTION_KEY: Record<string, keyof ProductFilterOptions> = {
  [DYNAMIC_FILTER_QUERY_FIELD_NAMES.brandCollection]: "brands",
  [DYNAMIC_FILTER_QUERY_FIELD_NAMES.stage]: "projectStages",
  [DYNAMIC_FILTER_QUERY_FIELD_NAMES.status]: "projectStatuses",
  [DYNAMIC_FILTER_QUERY_FIELD_NAMES.marketingDesigner]: "productDesignerMkts",
  [DYNAMIC_FILTER_QUERY_FIELD_NAMES.seriesName]: "seriesNames",
  [DYNAMIC_FILTER_QUERY_FIELD_NAMES.pdFamily]: "pdFamilyNames",
  [DYNAMIC_FILTER_QUERY_FIELD_NAMES.category]: "productcategories",
  [DYNAMIC_FILTER_QUERY_FIELD_NAMES.function]: "productFunctions",
  [DYNAMIC_FILTER_QUERY_FIELD_NAMES.baseCode]: "baseCodes",
  [DYNAMIC_FILTER_QUERY_FIELD_NAMES.pdCode]: "pdCodes",
  [DYNAMIC_FILTER_QUERY_FIELD_NAMES.stockCode]: "stockCodes",
  [DYNAMIC_FILTER_QUERY_FIELD_NAMES.primaryMaterial]: "primaryMaterials",
  [DYNAMIC_FILTER_QUERY_FIELD_NAMES.secondaryMaterial]: "secondaryMaterials",
  [DYNAMIC_FILTER_QUERY_FIELD_NAMES.slIntroDate]: "productIntroDatess",
  [DYNAMIC_FILTER_QUERY_FIELD_NAMES.tag]: "projectTags",
  [DYNAMIC_FILTER_QUERY_FIELD_NAMES.vendor]: "productSuppliers",
  [DYNAMIC_FILTER_QUERY_FIELD_NAMES.attachmentType]: "attachmentTypes",
  [DYNAMIC_FILTER_QUERY_FIELD_NAMES.quoteDate]: "quoteDates",
  [DYNAMIC_FILTER_QUERY_FIELD_NAMES.quoteApprovalType]: "quoteApprovalTypes",
  [DYNAMIC_FILTER_QUERY_FIELD_NAMES.assignment]: "assignments",
  [DYNAMIC_FILTER_QUERY_FIELD_NAMES.user]: "users",
  [DYNAMIC_FILTER_QUERY_FIELD_NAMES.itemStatus]: "itemStatuses",
};

const QUERY_FIELD_NAME_TO_PAYLOAD_KEY: Record<string, keyof DynamicFiltersRequest> = {
  [DYNAMIC_FILTER_QUERY_FIELD_NAMES.stockCode]: "stockCode",
  [DYNAMIC_FILTER_QUERY_FIELD_NAMES.baseCode]: "baseCode",
  [DYNAMIC_FILTER_QUERY_FIELD_NAMES.pdCode]: "pdCode",
  [DYNAMIC_FILTER_QUERY_FIELD_NAMES.slIntroDate]: "slIntroDates",
  [DYNAMIC_FILTER_QUERY_FIELD_NAMES.pdFamily]: "pdFamilies",
  [DYNAMIC_FILTER_QUERY_FIELD_NAMES.brandCollection]: "brandCollection",
  [DYNAMIC_FILTER_QUERY_FIELD_NAMES.stage]: "stages",
  [DYNAMIC_FILTER_QUERY_FIELD_NAMES.status]: "statuses",
  [DYNAMIC_FILTER_QUERY_FIELD_NAMES.marketingDesigner]: "marketingDesigners",
  [DYNAMIC_FILTER_QUERY_FIELD_NAMES.seriesName]: "seriesNames",
  [DYNAMIC_FILTER_QUERY_FIELD_NAMES.category]: "category",
  [DYNAMIC_FILTER_QUERY_FIELD_NAMES.function]: "functions",
  [DYNAMIC_FILTER_QUERY_FIELD_NAMES.primaryMaterial]: "primaryMaterial",
  [DYNAMIC_FILTER_QUERY_FIELD_NAMES.secondaryMaterial]: "secondaryMaterial",
  [DYNAMIC_FILTER_QUERY_FIELD_NAMES.vendor]: "vendors",
  [DYNAMIC_FILTER_QUERY_FIELD_NAMES.tag]: "tags",
  [DYNAMIC_FILTER_QUERY_FIELD_NAMES.itemStatus]: "itemStatus",
  [DYNAMIC_FILTER_QUERY_FIELD_NAMES.assignment]: "assignment",
  [DYNAMIC_FILTER_QUERY_FIELD_NAMES.user]: "user",
  [DYNAMIC_FILTER_QUERY_FIELD_NAMES.quoteDate]: "quoteDate",
  [DYNAMIC_FILTER_QUERY_FIELD_NAMES.quoteApprovalType]: "quoteApprovalType",
  [DYNAMIC_FILTER_QUERY_FIELD_NAMES.attachmentType]: "attachmentType",
};

export const getExcludedPayloadKeyForUiField = (
  uiFieldName?: string
): keyof DynamicFiltersRequest | null => {
  if (!uiFieldName) {
    return null;
  }

  const queryFieldName = resolveQueryFieldName(uiFieldName);
  if (!queryFieldName) {
    return null;
  }

  return QUERY_FIELD_NAME_TO_PAYLOAD_KEY[queryFieldName] ?? null;
};

export const DYNAMIC_FILTER_STATIC_FIELDS = new Set<string>([
  MULTI_SELECT_FIELD_NAMES.developmentType,
]);

export type DynamicFiltersRequest = {
  pageIndex?: number;
  pageSize?: number;
  projectCode?: number;
  stockCode?: string;
  baseCode?: string;
  pdCode?: string;
  seriesNames?: number[];
  pdFamilies?: string[];
  stages?: number[];
  statuses?: number[];
  tags?: number[];
  marketingDesigners?: number[];
  category?: number;
  functions?: number[];
  primaryMaterial?: number;
  secondaryMaterial?: number;
  brandCollection?: number;
  slIntroDates?: string[];
  developmentTypes?: number[];
  hideReleasedToInriver?: boolean;
  hideArchivedDevelopment?: boolean;
  vendors?: number[];
  itemStatus?: number;
  isActive?: boolean;
  assignment?: number;
  user?: number;
  quoteDate?: string;
  quoteApprovalType?: number;
  attachmentType?: number;
  sortBy?: string;
  sortDirection?: string | number;
};

export type FilterValuesState = SelectedFilters & {
  assignmentId?: number | null;
  attachmentType?: number | null;
  attachmentTypeId?: number | null;
  slIntroDates?: string[] | null;
  user?: number | null;
  quoteDate?: string | null;
  quoteApprovalType?: number | null;
  vendors?: number[] | null;
  itemStatus?: number | null;
};

export const isFilterValueSelected = (value: unknown): boolean => {
  if (value === null || value === undefined || value === "") {
    return false;
  }

  if (Array.isArray(value)) {
    return value.length > 0;
  }

  return true;
};

export const normalizeMultiSelectPayloadValue = <T>(
  value: T | T[] | null | undefined
): T[] | null => {
  if (value === null || value === undefined) {
    return null;
  }

  const values = Array.isArray(value) ? [...value] : [value];
  const filteredValues = values.filter(
    (item) => item !== null && item !== undefined && item !== ""
  );

  return filteredValues.length > 0 ? filteredValues : null;
};

type MultiSelectFieldConfig = {
  valueKey: keyof FilterValuesState;
  hasId: boolean;
};

const MULTI_SELECT_VALUE_FIELDS: MultiSelectFieldConfig[] = [
  { valueKey: "stages", hasId: true },
  { valueKey: "statuses", hasId: true },
  { valueKey: "marketingDesigners", hasId: true },
  { valueKey: "functions", hasId: true },
  { valueKey: "seriesNames", hasId: true },
  { valueKey: "pdFamilies", hasId: false },
  { valueKey: "slIntroDates", hasId: false },
  { valueKey: "vendors", hasId: true },
  { valueKey: "tags", hasId: true },
  { valueKey: "developmentTypes", hasId: true },
];

export const enrichFilterValuesWithMultiSelectSelections = (
  filterValues: FilterValuesState,
  selectedOptions: Record<string, unknown> = {}
): FilterValuesState => {
  const enriched: FilterValuesState = { ...filterValues };

  MULTI_SELECT_VALUE_FIELDS.forEach(({ valueKey, hasId }) => {
    const selectedItems = selectedOptions[valueKey];
    if (!Array.isArray(selectedItems) || selectedItems.length === 0) {
      return;
    }

    const allSelectedValues = selectedItems.map((item) => {
      if (hasId) {
        return typeof item === "object" && item !== null && "id" in item
          ? (item as { id: number }).id
          : item;
      }

      return typeof item === "string" ? item : item;
    });

    (enriched as Record<string, unknown>)[valueKey] = allSelectedValues;
  });

  return enriched;
};

const isDefaultDevelopmentTypes = (developmentTypes?: number[] | null): boolean => {
  if (!developmentTypes?.length) {
    return true;
  }

  return (
    developmentTypes.length === 1 &&
    developmentTypes[0] === PRODUCT_TYPE_DROPDOWN_OPTIONS[0].id
  );
};

export const hasActiveFilterSelections = (
  filterValues: FilterValuesState
): boolean => {
  const filterFields: unknown[] = [
    filterValues.brandCollection,
    filterValues.stages,
    filterValues.statuses,
    filterValues.marketingDesigners,
    filterValues.seriesNames,
    filterValues.pdFamilies,
    filterValues.category,
    filterValues.functions,
    filterValues.baseCode,
    filterValues.pdCode,
    filterValues.stockCode,
    filterValues.primaryMaterial,
    filterValues.secondaryMaterial,
    filterValues.slIntroDates ?? filterValues.introDates,
    filterValues.tags,
    filterValues.vendors,
    filterValues.itemStatus,
    filterValues.quoteDate,
    filterValues.quoteApprovalType,
    filterValues.attachmentType ?? filterValues.attachmentTypeId,
    filterValues.assignment ?? filterValues.assignmentId,
    filterValues.userId ?? filterValues.user,
    filterValues.projectCode,
  ];

  if (filterFields.some(isFilterValueSelected)) {
    return true;
  }

  if (!isDefaultDevelopmentTypes(filterValues.developmentTypes)) {
    return true;
  }

  if (filterValues.hideReleasedToInriver === false) {
    return true;
  }

  if (filterValues.hideArchivedDevelopment === false) {
    return true;
  }

  return false;
};

export const resolveIsActiveForViewMode = (viewMode: string): boolean | null => {
  if (viewMode === PermissionsFriendlyNames[Permissions.ViewProducts]) {
    return true;
  }
  if (viewMode === VIEW_INACTIVE_PRODUCTS) {
    return false;
  }
  return null;
};

export const resolveQueryFieldName = (uiFieldName: string): string | null =>
  UI_FIELD_TO_QUERY_FIELD_NAME[uiFieldName] ?? null;

/** @deprecated Use resolveQueryFieldName */
export const resolveApiFieldName = resolveQueryFieldName;

export const buildDynamicFiltersUrl = (uiFieldName?: string): string => {
  const baseUrl = "/Item/dynamic-filters-dropdown";

  if (!uiFieldName) {
    return baseUrl;
  }

  const apiFieldName = resolveQueryFieldName(uiFieldName);
  if (!apiFieldName) {
    return baseUrl;
  }

  const params = new URLSearchParams();
  params.set(DYNAMIC_FILTERS_FIELD_QUERY_PARAM, apiFieldName);
  return `${baseUrl}?${params.toString()}`;
};

export const buildDynamicFiltersRequest = (
  filterValues: FilterValuesState,
  viewMode: string,
  openedUiFieldName?: string
): DynamicFiltersRequest => {
  const assignment =
    filterValues.assignment ?? filterValues.assignmentId ?? null;
  const attachmentType =
    filterValues.attachmentType ?? filterValues.attachmentTypeId ?? null;
  const slIntroDates =
    filterValues.slIntroDates ?? filterValues.introDates ?? null;
  const projectCode = filterValues.projectCode
    ? Number(filterValues.projectCode)
    : null;

  const excludedPayloadKey = getExcludedPayloadKeyForUiField(openedUiFieldName);
  const request: DynamicFiltersRequest = {};

  const addIfSelected = <K extends keyof DynamicFiltersRequest>(
    key: K,
    value: DynamicFiltersRequest[K] | null | undefined
  ) => {
    if (key === excludedPayloadKey) {
      return;
    }

    if (isFilterValueSelected(value)) {
      request[key] = value as DynamicFiltersRequest[K];
    }
  };

  const addMultiSelectIfSelected = <K extends keyof DynamicFiltersRequest>(
    key: K,
    value: unknown
  ) => {
    if (key === excludedPayloadKey) {
      return;
    }

    const normalizedValues = normalizeMultiSelectPayloadValue(value);
    if (normalizedValues) {
      (request as Record<string, unknown>)[key] = normalizedValues;
    }
  };

  addIfSelected("projectCode", projectCode);
  addIfSelected("stockCode", filterValues.stockCode);
  addIfSelected("baseCode", filterValues.baseCode);
  addIfSelected("pdCode", filterValues.pdCode);
  addMultiSelectIfSelected("seriesNames", filterValues.seriesNames);
  addMultiSelectIfSelected("pdFamilies", filterValues.pdFamilies);
  addMultiSelectIfSelected("stages", filterValues.stages);
  addMultiSelectIfSelected("statuses", filterValues.statuses);
  addMultiSelectIfSelected("tags", filterValues.tags);
  addMultiSelectIfSelected("marketingDesigners", filterValues.marketingDesigners);
  addIfSelected("category", filterValues.category);
  addMultiSelectIfSelected("functions", filterValues.functions);
  addIfSelected("primaryMaterial", filterValues.primaryMaterial);
  addIfSelected("secondaryMaterial", filterValues.secondaryMaterial);
  addIfSelected("brandCollection", filterValues.brandCollection);
  addMultiSelectIfSelected("slIntroDates", slIntroDates);
  addMultiSelectIfSelected("vendors", filterValues.vendors);
  addIfSelected("itemStatus", filterValues.itemStatus);
  addIfSelected("assignment", assignment);
  addIfSelected("user", filterValues.userId ?? filterValues.user);
  addIfSelected("quoteDate", filterValues.quoteDate);
  addIfSelected("quoteApprovalType", filterValues.quoteApprovalType);
  addIfSelected("attachmentType", attachmentType);

  if (!isDefaultDevelopmentTypes(filterValues.developmentTypes)) {
    addMultiSelectIfSelected("developmentTypes", filterValues.developmentTypes);
  }

  if (filterValues.hideReleasedToInriver === false) {
    request.hideReleasedToInriver = false;
  }

  if (filterValues.hideArchivedDevelopment === false) {
    request.hideArchivedDevelopment = false;
  }

  const isActive = resolveIsActiveForViewMode(viewMode) ?? filterValues.isActive ?? null;
  if (isActive !== null) {
    request.isActive = isActive;
  }

  return request;
};

export const getFilterContextCacheKey = (
  filterValues: Record<string, unknown>,
  viewMode: string,
  uiFieldName?: string
): string => `${viewMode}:${uiFieldName ?? "all"}:${JSON.stringify(filterValues)}`;

export type DynamicFilterFieldResponse = Array<string | Record<string, unknown>>;

const STRING_ARRAY_FILTER_OPTION_KEYS = new Set<keyof ProductFilterOptions>([
  "stockCodes",
  "baseCodes",
  "pdCodes",
  "pdFamilyNames",
  "productIntroDatess",
]);

export const normalizeDynamicFieldResponseToStringArray = (
  data: DynamicFilterFieldResponse
): string[] => {
  return data
    .map((item) => {
      if (typeof item === "string") {
        return item;
      }

      if (typeof item === "object" && item !== null) {
        const value = item.name ?? item.description;
        return typeof value === "string" ? value : null;
      }

      return null;
    })
    .filter((item): item is string => Boolean(item));
};

export const normalizeDynamicFieldResponseToObjectArray = (
  data: DynamicFilterFieldResponse
): Array<Record<string, unknown>> => {
  return data.reduce<Array<Record<string, unknown>>>((normalizedItems, item, index) => {
    if (typeof item === "string") {
      normalizedItems.push({ id: item, name: item });
      return normalizedItems;
    }

    if (typeof item !== "object" || item === null) {
      return normalizedItems;
    }

    const name = item.name ?? item.description;
    const id = item.id ?? name ?? index;

    normalizedItems.push({
      ...item,
      id,
      ...(name !== undefined && { name }),
      ...(item.description !== undefined
        ? { description: item.description }
        : name !== undefined && { description: name }),
    });

    return normalizedItems;
  }, []);
};

const applyFieldOptionPostProcessing = (
  optionKey: keyof ProductFilterOptions,
  optionValues: unknown,
  viewMode: string
): Partial<ProductFilterOptions> => {
  const result: Partial<ProductFilterOptions> = {};

  if (optionKey === "productIntroDatess") {
    const productIntroDates = [
      ...(Array.isArray(optionValues) ? optionValues : []),
    ] as string[];
    if (!productIntroDates.includes(NO_SL_INTRO_DATE)) {
      productIntroDates.push(NO_SL_INTRO_DATE);
    }
    sortCVLFieldOptions({ productIntroDatess: productIntroDates }, ["productIntroDatess"]);
    result.productIntroDatess = productIntroDates;
    return result;
  }

  if (optionKey === "productSuppliers") {
    result.productSuppliers = [
      ...((optionValues as ProductFilterOptions["productSuppliers"]) || []),
      { id: 0, name: NO_VENDOR },
    ];
    return result;
  }

  if (optionKey === "projectTags") {
    if (
      ![PermissionsFriendlyNames[Permissions.ProjectOverviewDashboard], VIEW_TOOLINGS, VIEW_PRODUCT_ASSIGNMENT].includes(viewMode)
    ) {
      result.projectTags = [
        ...((optionValues as ProductFilterOptions["projectTags"]) || []),
        NO_TAGS_OPTION,
      ];
    } else {
      result.projectTags = optionValues as ProductFilterOptions["projectTags"];
    }
    return result;
  }

  (result as Record<string, unknown>)[optionKey] = optionValues;
  return result;
};

export const getEmptyFilterOptionsForField = (
  uiFieldName: string
): Partial<ProductFilterOptions> => {
  const queryFieldName = resolveQueryFieldName(uiFieldName);
  if (!queryFieldName) {
    return {};
  }

  const optionKey = QUERY_FIELD_NAME_TO_FILTER_OPTION_KEY[queryFieldName];
  if (!optionKey) {
    return {};
  }

  return { [optionKey]: [] };
};

export const mapDynamicFieldResponseToFilterOptions = (
  uiFieldName: string,
  data: DynamicFilterFieldResponse,
  viewMode: string
): Partial<ProductFilterOptions> => {
  const queryFieldName = resolveQueryFieldName(uiFieldName);
  if (!queryFieldName || !Array.isArray(data)) {
    return {};
  }

  const optionKey = QUERY_FIELD_NAME_TO_FILTER_OPTION_KEY[queryFieldName];
  if (!optionKey) {
    return {};
  }

  const optionValues = STRING_ARRAY_FILTER_OPTION_KEYS.has(optionKey)
    ? normalizeDynamicFieldResponseToStringArray(data)
    : normalizeDynamicFieldResponseToObjectArray(data);

  return applyFieldOptionPostProcessing(optionKey, optionValues, viewMode);
};

export const extractFieldFilterOptions = (
  uiFieldName: string,
  data: ProductFilterOptions | DynamicFilterFieldResponse,
  viewMode: string
): Partial<ProductFilterOptions> => {
  if (Array.isArray(data)) {
    return mapDynamicFieldResponseToFilterOptions(uiFieldName, data, viewMode);
  }

  const queryFieldName = resolveQueryFieldName(uiFieldName);
  if (!queryFieldName) {
    return {};
  }

  const optionKey = QUERY_FIELD_NAME_TO_FILTER_OPTION_KEY[queryFieldName];
  if (!optionKey || data[optionKey] === undefined) {
    return {};
  }

  return applyFieldOptionPostProcessing(optionKey, data[optionKey], viewMode);
};
